import type { LocationData } from "../../../mocks/locationData";

interface LocationWhyUsProps {
  location: LocationData;
  onBookNow: () => void;
}

const reasons = [
  {
    icon: "ri-time-line",
    title: "Same-Day Collection",
    desc: "Book before 11am and we'll typically collect the same day. No waiting around for skip permits.",
  },
  {
    icon: "ri-price-tag-3-line",
    title: "Fixed, Transparent Price",
    desc: "The price you see is the price you pay. We price by van space — no itemised charges, no hidden fees.",
  },
  {
    icon: "ri-shield-check-line",
    title: "Fully Licensed",
    desc: "Environment Agency Waste Carrier Licence CBDU 634440. All waste is legally and responsibly disposed of.",
  },
  {
    icon: "ri-leaf-line",
    title: "Eco-Responsible",
    desc: "Over 95% of everything we collect is diverted from landfill through recycling, reuse and energy recovery.",
  },
  {
    icon: "ri-user-star-line",
    title: "4.8★ on Trustpilot",
    desc: "200+ verified reviews. We're rated excellent because we genuinely care about every collection.",
  },
  {
    icon: "ri-map-pin-2-line",
    title: `Local to ${"{city}"}`,
    desc: "Our local crews are DBS checked, fully vetted, and directly employed — no subcontractors. They know the streets, the parking challenges and the recycling facilities in your area.",
  },
];

export default function LocationWhyUs({ location, onBookNow }: LocationWhyUsProps) {
  const filledReasons = reasons.map((r) => ({
    ...r,
    desc: r.desc.replace("{city}", location.city),
    title: r.title.replace("{city}", location.city),
  }));

  return (
    <section className="bg-white py-20 px-6 lg:px-10">
      <div className="max-w-screen-xl mx-auto">
        <div className="text-center mb-14">
          <p className="text-xs font-black text-cl-green uppercase tracking-widest mb-2">Why Cleario?</p>
          <h2 className="text-3xl md:text-4xl font-black text-cl-dark leading-tight">
            Why Choose Cleario in{" "}
            <span className="text-cl-green">{location.city}</span>
          </h2>
          <p className="text-gray-500 font-semibold mt-3 max-w-xl mx-auto">
            We've built our reputation across the UK by doing the basics brilliantly — fast, fair and responsible.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {filledReasons.map((reason) => (
            <div
              key={reason.title}
              className="bg-cl-tint rounded-2xl p-6 thick-border cartoon-shadow group hover:-translate-y-1 transition-all duration-200"
            >
              <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-cl-lime border-2 border-cl-green mb-4 group-hover:bg-cl-green transition-colors">
                <i className={`${reason.icon} text-xl text-cl-dark group-hover:text-white transition-colors`}></i>
              </div>
              <h3 className="text-cl-dark font-black text-base mb-2">{reason.title}</h3>
              <p className="text-gray-500 text-sm font-semibold leading-relaxed">{reason.desc}</p>
            </div>
          ))}
        </div>

        {/* CTA strip */}
        <div className="mt-14 bg-cl-dark rounded-2xl p-8 flex flex-col md:flex-row items-center justify-between gap-6 thick-border cartoon-shadow">
          <div>
            <p className="text-cl-lime font-black text-xl mb-1">Ready to clear the clutter in {location.city}?</p>
            <p className="text-white/60 font-semibold text-sm">Get an instant fixed price — takes under 2 minutes</p>
          </div>
          <div className="flex gap-3 flex-shrink-0">
            <button
              onClick={onBookNow}
              className="bg-cl-green text-white font-black px-7 py-3.5 rounded-xl text-sm cartoon-btn-green whitespace-nowrap"
            >
              Book Online
            </button>
            <a
              href="tel:03335775445"
              className="flex items-center gap-2 bg-white/10 border-2 border-white/20 text-white font-black px-6 py-3.5 rounded-xl text-sm whitespace-nowrap cartoon-btn"
            >
              <i className="ri-phone-fill text-cl-lime text-sm"></i>
              Call Us
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}
